import { Head, Link } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { ArrowLeft, Edit } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';

interface SplitJob {
  id: number;
  start_page?: number;
  end_page?: number;
  header_search_string?: string;
  split_rules?: string;
  status: string;
  start_pages?: any;
  processing_job_id?: string;
  job_id?: string;
}

interface Document {
  id: number;
  name: string;
  file_path?: string;
  split_jobs: SplitJob[];
}

interface ProgressMessage {
  timestamp: number;
  message: string;
}

interface SplitJobShowProps {
  document: Document;
  splitJob: SplitJob;
  jobId?: string;
  progressMessages?: ProgressMessage[];
}

export default function SplitJobShow({ document, splitJob, jobId, progressMessages = [] }: SplitJobShowProps) {
  const [liveMessages, setLiveMessages] = useState<ProgressMessage[]>(progressMessages);
  const [currentJobStatus, setCurrentJobStatus] = useState(splitJob.start_pages ? 'Complete' : 'Processing');
  const messagesRef = useRef<HTMLDivElement>(null);

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
    } as const;
    
    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  // Real-time progress updates using ActionCable
  useEffect(() => {
    if (jobId && !splitJob.start_pages) {
      // Initialize ActionCable subscription
      const cable = (window as any).App?.cable;
      if (cable) {
        const subscription = cable.subscriptions.create(
          { 
            channel: "JobProgressChannel", 
            job_id: jobId 
          },
          {
            received: function(data: any) {
              const newMessage: ProgressMessage = {
                timestamp: data.timestamp,
                message: data.message
              };
              
              setLiveMessages(prev => [...prev, newMessage]);
              
              // Auto-scroll to bottom
              if (messagesRef.current) {
                messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
              }
              
              // Update status if job is complete
              if (data.message.includes("Results saved to database")) {
                setCurrentJobStatus("Complete");
              }
            }
          }
        );
        
        return () => {
          if (subscription) {
            cable.subscriptions.remove(subscription);
          }
        };
      } else {
        console.warn('ActionCable not available');
      }
    }
  }, [jobId, splitJob.start_pages]);

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp * 1000).toTimeString().split(' ')[0];
  };

  const renderStartPages = () => {
    if (!splitJob.start_pages) return null;

    let startPages: Record<string, any>;
    
    try {
      if (typeof splitJob.start_pages === 'string') {
        // Handle string format that might use Ruby hash syntax
        startPages = JSON.parse(splitJob.start_pages.replace(/=>/g, ':'));
      } else {
        startPages = splitJob.start_pages;
      }
    } catch (error) {
      console.error('Error parsing start_pages:', error);
      return <p className="text-red-600">Error parsing results</p>;
    }

    const sortedPages = Object.entries(startPages)
      .filter(([, heading]) => String(heading).trim().toLowerCase() !== 'no')
      .sort(([a], [b]) => parseInt(a) - parseInt(b));

    return (
      <div className="space-y-2">
        {sortedPages.map(([page, heading]) => (
          <div key={page} className="flex items-center py-1">
            <span className="font-semibold text-gray-700 w-20">Page {page}:</span>
            <span className="text-gray-900">{String(heading)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Head title={`Split Job - ${document.name}`} />
      
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: 'Split Job', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">PDF Processing Job</h1>
            <div className="flex gap-3">
              <Button asChild variant="outline">
                <Link href={`/documents/${document.id}`}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Document
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link href={`/documents/${document.id}/split_jobs/${splitJob.id}/edit`}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Split Job
                </Link>
              </Button>
            </div>
          </div>

          {/* Job Details */}
          <Card>
            <CardHeader>
              <CardTitle>Job Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <span className="font-semibold">Document: </span>
                <span>{document.file_path?.split('/').pop() || document.name}</span>
              </div>
              
              {splitJob.header_search_string && (
                <div>
                  <span className="font-semibold">Search String: </span>
                  <span>{splitJob.header_search_string}</span>
                </div>
              )}
              
              <div>
                <span className="font-semibold">Page Range: </span>
                <span>{splitJob.start_page || 1} to {splitJob.end_page || 'End'}</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="font-semibold">Status: </span>
                <Badge className={getStatusBadge(splitJob.status)} id="job-status">
                  {currentJobStatus}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Processing Log */}
          {(jobId || liveMessages.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>Processing Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  ref={messagesRef}
                  className="bg-gray-50 border border-gray-300 p-3 font-mono text-sm h-72 overflow-y-auto"
                  id="progress-messages"
                >
                  {liveMessages.length > 0 ? (
                    <div className="space-y-1">
                      {liveMessages.map((msg: ProgressMessage, index: number) => (
                        <div key={index} className="log-entry mb-1">
                          <span className="timestamp text-gray-500 mr-2">{formatTimestamp(msg.timestamp)}</span>
                          <span className="message">{msg.message}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="log-entry">Waiting for processing to begin...</div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Alert for jobs without real-time tracking */}
          {!jobId && !liveMessages.length && (
            <Card>
              <CardContent className="bg-yellow-50 border-yellow-200 p-4">
                <p className="text-yellow-800">
                  This job was processed before real-time progress tracking was available.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Processing Results */}
          {splitJob.start_pages && (
            <Card>
              <CardHeader>
                <CardTitle>Processing Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 p-4 rounded border max-h-96 overflow-y-auto">
                  {renderStartPages()}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
